import { Send, Sparkles } from 'lucide-react'
import { Button } from '~/components/ui/button'
import { Markdown } from '~/components/ui/markdown'
import { ChatThinking } from '~/components/chat/chat-thinking'
import { Typewriter } from '~/components/chat/typewriter'

export interface BoardAiMessage {
    role: 'user' | 'ai'
    content: string
}

/**
 * AI 빌더 패널(우측 도킹) — 메시지 렌더 + 입력창 + 전송.
 * 상태(메시지·입력·busy)는 부모가 소유하고 표시·입력만 props로 받는다.
 */
export function BoardAiPanel({
    messages,
    input,
    onInput,
    busy,
    onSend,
}: {
    messages: BoardAiMessage[]
    input: string
    onInput: (value: string) => void
    busy: boolean
    onSend: () => void
}) {
    return (
        <aside className='border-border bg-card flex h-[560px] w-80 shrink-0 flex-col rounded-md border'>
            <div className='border-border flex items-center gap-2 border-b px-4 py-2.5'>
                <Sparkles className='text-primary size-4' />
                <span className='text-foreground text-sm font-semibold'>
                    AI 빌더
                </span>
            </div>
            <div className='min-h-0 flex-1 space-y-2.5 overflow-y-auto p-3'>
                {messages.length === 0 ? (
                    <p className='text-muted-foreground text-xs leading-relaxed'>
                        자연어로 보드를 만들거나 데이터를 물어보세요.
                        <br />
                        예: "온도 시계열이랑 평균 통계 카드 넣어줘", "지난주
                        습도 최대값은?"
                    </p>
                ) : (
                    messages.map((m, i) => (
                        <div
                            key={i}
                            className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                            <div
                                className={`max-w-[90%] min-w-0 rounded-md px-2.5 py-1.5 text-xs ${
                                    m.role === 'user'
                                        ? 'bg-primary text-primary-foreground whitespace-pre-wrap'
                                        : 'border-border bg-background text-foreground border'
                                }`}>
                                {m.role === 'user' ? (
                                    m.content
                                ) : i === messages.length - 1 ? (
                                    <Typewriter text={m.content} />
                                ) : (
                                    <Markdown>{m.content}</Markdown>
                                )}
                            </div>
                        </div>
                    ))
                )}
                {busy ? (
                    <ChatThinking
                        phrases={[
                            '요청을 이해하는 중',
                            '보드를 구성하는 중',
                            '카드를 배치하는 중',
                        ]}
                    />
                ) : null}
            </div>
            <form
                onSubmit={(e) => {
                    e.preventDefault()
                    onSend()
                }}
                className='border-border flex items-center gap-1.5 border-t p-2'>
                <input
                    value={input}
                    onChange={(e) => onInput(e.target.value)}
                    placeholder='어떤 보드를 만들까요?'
                    className='border-border bg-card text-foreground flex-1 rounded-md border px-2.5 py-1.5 text-xs outline-none'
                />
                <Button
                    type='submit'
                    size='sm'
                    loading={busy}
                    disabled={!input.trim()}>
                    {!busy && <Send />}
                </Button>
            </form>
        </aside>
    )
}
